import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ListingService from '../services/ListingService';
import ExclusiveCard from './ExclusiveCard';
import HeroSection from './HeroSection';
import '../css/exclusive.css';

const ExclusiveListingsPage = ({ agent, onSelectProperty }) => {
  const { linkKey } = useParams();
  const navigate = useNavigate();

  const [searchInfo, setSearchInfo] = useState(null);
  const [listings, setListings] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('latest');

  const fetchExclusiveData = async (pageNum, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    try {
      const params = { page: pageNum, size: 12, sortBy };
      const res = await ListingService.getExclusiveListings(linkKey, params);
      if (res) {
        if (append) {
          setListings(prev => [...prev, ...(res.listings || [])]);
        } else {
          setListings(res.listings || []);
        }
        setTotalCount(res.totalCount || 0);
        setPage(res.page || 0);
        setTotalPages(res.totalPages || 0);
      }
    } catch (err) {
      console.error('Failed to fetch exclusive listings:', err);
      setError('Failed to load listings. Please make sure the link is valid.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadInfo = async () => {
    try {
      const info = await ListingService.getExclusiveInfo(linkKey);
      if (info && info.term_name) {
        setSearchInfo(info);
        setError(null);
      } else {
        setError('This link is invalid or has expired.');
      }
    } catch (err) {
      console.error('Failed to fetch exclusive search info:', err);
      setError('This exclusive link could not be loaded.');
    }
  };

  useEffect(() => {
    if (linkKey) loadInfo();
  }, [linkKey]);

  useEffect(() => {
    if (searchInfo) fetchExclusiveData(0, false);
  }, [searchInfo, sortBy]);

  const handlePageChange = (pageNum) => {
    if (pageNum >= 0 && pageNum < totalPages) {
      fetchExclusiveData(pageNum, false);
      window.scrollTo({ top: 300, behavior: 'smooth' });
    }
  };

  const handleLoadMore = () => {
    if (page + 1 < totalPages) {
      fetchExclusiveData(page + 1, true);
    }
  };

  const getPropertyWhatsappUrl = (prop) => {
    const text = `Hi Nicole, I'm interested in the property listing #${prop.id}: ${prop.title} in ${prop.area} (${prop.price}). Could you please share more details?`;
    return `https://wa.me/${agent?.whatsappNumber || '60163288120'}?text=${encodeURIComponent(text)}`;
  };

  const handleGoHome = () => {
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // SEO meta tags
  useEffect(() => {
    if (searchInfo?.term_name) {
      document.title = `${searchInfo.term_name} – Exclusive Listings`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) {
        meta.setAttribute('content', searchInfo.desc || 'Handpicked exclusive property listings.');
      }
    }
  }, [searchInfo]);

  if (error) {
    return (
      <div className="all-listings-container container py-5 text-center" style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <i className="bi bi-exclamation-triangle-fill text-warning mb-4" style={{ fontSize: '3rem' }}></i>
        <h2 style={{ color: '#d4af37', fontFamily: 'Montserrat, sans-serif' }}>Exclusive Link Unavailable</h2>
        <p className="text-secondary mt-2 mb-4" style={{ maxWidth: '500px' }}>{error}</p>
        <button onClick={handleGoHome} className="btn-filter-submit" style={{ width: 'auto', padding: '0.8rem 2rem' }}>RETURN TO HOME</button>
      </div>
    );
  }

  return (
    <div className="exclusive-page">
      {/* Hero Section */}
      <HeroSection agent={agent} />

      {/* Exclusive Banner Section */}
      <section
        className="exclusive-hero"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(9,13,22,0.9), rgba(16,24,38,0.9)), url(${searchInfo?.banner_image || ''})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="exclusive-hero-content">
          <h1 className="exclusive-hero-title">{searchInfo?.term_name || 'Exclusive Curated Properties'}</h1>
          <p className="exclusive-hero-desc">{searchInfo?.desc || "Handpicked real estate listings tailored to your preferences. Feel free to contact me if you'd like more details about any of these properties."}</p>
        </div>
      </section>

      <div className="exclusive-content container py-4">
        <div className="exclusive-main">
          <div style={{ marginBottom: '1.5rem' }}>
            <span className="text-secondary small">Curated <strong>{totalCount}</strong> exclusive {totalCount === 1 ? 'listing' : 'listings'} for you, our valued client.</span>
            <div className="sort-controls" style={{ margin: 0, marginTop: '0.75rem', justifyContent: 'flex-start' }}>
              <label htmlFor="sortBySelect" className="sort-label">Sort By:</label>
              <select id="sortBySelect" value={sortBy} onChange={e => setSortBy(e.target.value)} className="sort-select">
                <option value="latest">Latest</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
              </select>
            </div>
          </div>

          <div className="listings-grid">
            {loading ? (
              <div className="listings-loading-state"><i className="bi bi-arrow-repeat spin"></i> Loading exclusive listings...</div>
            ) : (
              listings.map(item => (
                <ExclusiveCard key={item.id} listing={item} agent={agent} onSelectProperty={onSelectProperty} whatsappUrl={getPropertyWhatsappUrl(item)} />
              ))
            )}
          </div>

          {page + 1 < totalPages && (
            <div className="exclusive-load-more"><button onClick={handleLoadMore} disabled={loadingMore}>Load More</button></div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExclusiveListingsPage;
